import { useMemo } from "react";
import { useParams } from "react-router";
import { Toast } from "@sscs/ui";
import { getQueryStatus } from "@sscs/utils";
import { PERMISSION } from "Constants";
import { useAppDispatch } from "Hooks";
import { useGetWhoamiQuery } from "Services";
import { apiErrorToast } from "Utils";
import {
  useGetGroupPolicyQuery,
  useUpdateGroupPolicyMutation,
} from "../services/policies.api";
import { resetConfiguration } from "../store/policyConfigSlice";
import {
  PolicyConfig,
  PolicyConfigurationScope,
  UpdateGroupRequestBody,
} from "../types";

interface IUseGroupConfig {
  configScope: PolicyConfigurationScope;
}

const useGroupConfig = ({ configScope }: IUseGroupConfig): PolicyConfig => {
  const { groupId } = useParams();
  const dispatch = useAppDispatch();
  const { data: whoami } = useGetWhoamiQuery();

  const findGroupName = (): string | undefined => {
    return whoami?.org_groups.find((group) => group.group_id === groupId)?.name;
  };

  const groupName = useMemo(findGroupName, [groupId, whoami?.org_groups]);

  const {
    isLoading,
    isFetching,
    isError,
    error,
    data: policies,
  } = useGetGroupPolicyQuery({ groupId: groupId ?? "" }, { skip: !groupId });

  const status = getQueryStatus({
    isLoading,
    isFetching,
    isError,
    error,
    data: policies,
  });

  const [updateGroup] = useUpdateGroupPolicyMutation();

  const updatePolicy = async (body: UpdateGroupRequestBody) => {
    try {
      await updateGroup({
        groupId: groupId ?? "",
        body,
      }).unwrap();
      dispatch(resetConfiguration(groupId ?? ""));

      Toast.show({
        title: "Policy configuration changes saved",
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, "Could not save policy configuration changes");
    }
  };

  return {
    entityName: groupName ?? "",
    configScope,
    status,
    isError,
    updatePolicy,
    policies: policies?.policies ?? {},
    reprocessEstimate: policies?.estimate ?? 0,
    rlScanLevel: policies?.rl_scan_level ?? {
      inherited_level: null,
      current_level: null,
    },
    autoApprovalEnabled: policies?.auto_approval_enabled ?? false,
    permissions: [PERMISSION.CONFIGURE_GROUP_POLICY_PROFILE],
  };
};

export default useGroupConfig;
